/**
 * Floating batch upload progress panel.
 *
 * Renders as a fixed-position panel in the bottom-right corner via React
 * portal. Shows per-file progress, overall stats, and supports minimize,
 * cancel, retry, and clear actions. Persists across page navigation.
 */

import type { BatchStats, UploadItem } from '@/hooks/useBatchUpload';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ChevronDown,
  ChevronUp,
  UploadCloud,
  X,
} from 'lucide-react';
import React, { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-hot-toast';

import FileRow from './batchUpload/FileRow';
import { TERMINAL } from './batchUpload/helpers';
import OverallProgressBar from './batchUpload/OverallProgressBar';
import UploadStatsFooter from './batchUpload/UploadStatsFooter';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface Props {
  items: UploadItem[];
  stats: BatchStats;
  isActive: boolean;
  isPanelMinimized: boolean;
  onCancelItem: (id: string) => void;
  onCancelAll: () => void;
  onRemoveItem: (id: string) => void;
  onClearCompleted: () => void;
  onRetryItem: (id: string) => void;
  onToggleMinimize: () => void;
  onHidePanel: () => void;
}

// ---------------------------------------------------------------------------
// Panel component
// ---------------------------------------------------------------------------

const BatchUploadPanel: React.FC<Props> = ({
  items,
  stats,
  isActive,
  isPanelMinimized,
  onCancelItem,
  onCancelAll,
  onRemoveItem,
  onClearCompleted,
  onRetryItem,
  onToggleMinimize,
  onHidePanel,
}) => {
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const handleClose = useCallback(() => {
    if (isActive) {
      setShowCancelConfirm(true);
    } else {
      onClearCompleted();
      onHidePanel();
    }
  }, [isActive, onClearCompleted, onHidePanel]);

  const confirmCancelAll = useCallback(() => {
    onCancelAll();
    setShowCancelConfirm(false);
    toast('All uploads cancelled', { icon: '🛑' });
  }, [onCancelAll]);

  const processed = stats.completed + stats.duplicates + stats.failed;
  // Honest done-state breakdown — duplicates are "already in library", not
  // "uploaded" (conflating them is what read as "only 2 of 6 ingested").
  const doneSummary =
    [
      `${stats.completed} added`,
      stats.duplicates > 0 ? `${stats.duplicates} already in library` : null,
      stats.failed > 0 ? `${stats.failed} failed` : null,
    ]
      .filter(Boolean)
      .join(' · ') || 'No files uploaded';
  const headerText = isActive
    ? `Uploading… ${processed} of ${stats.total} (${stats.inProgress} in flight)`
    : doneSummary;

  const hasTerminal = items.some((i) => TERMINAL.has(i.status));

  const panel = (
    <motion.div
      initial={{ opacity: 0, y: 40, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 40, scale: 0.95 }}
      transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      className="fixed bottom-6 right-6 z-50 w-96"
    >
        <div className="bg-gray-900/95 backdrop-blur-xl border border-white/[0.06] rounded-xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-white/[0.04]">
            <div className="flex items-center gap-2">
              <UploadCloud className="h-4 w-4 text-brand-400" />
              <span className="text-xs font-medium text-gray-200">{headerText}</span>
            </div>
            <div className="flex items-center gap-1">
              {hasTerminal && !isActive && (
                <button
                  onClick={onClearCompleted}
                  className="text-[10px] text-gray-500 hover:text-gray-300 transition-colors px-1.5 py-0.5 rounded hover:bg-white/[0.04]"
                >
                  Clear
                </button>
              )}
              <button
                onClick={onToggleMinimize}
                className="p-1 text-gray-500 hover:text-gray-300 transition-colors rounded hover:bg-white/[0.04]"
                title={isPanelMinimized ? 'Expand' : 'Minimize'}
              >
                {isPanelMinimized ? (
                  <ChevronUp className="h-3.5 w-3.5" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5" />
                )}
              </button>
              <button
                onClick={handleClose}
                className="p-1 text-gray-500 hover:text-gray-300 transition-colors rounded hover:bg-white/[0.04]"
                title="Close"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {/* Body (hidden when minimized) */}
          <AnimatePresence>
            {!isPanelMinimized && (
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: 'auto' }}
                exit={{ height: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                {/* Overall progress bar */}
                <OverallProgressBar stats={stats} isActive={isActive} />

                {/* Cancel confirmation */}
                {showCancelConfirm && (
                  <div className="px-3 py-2 bg-red-500/10 border-b border-red-500/20">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] text-red-300">
                        Cancel {stats.inProgress + stats.queued} active uploads?
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setShowCancelConfirm(false)}
                          className="text-[10px] text-gray-400 hover:text-white transition-colors"
                        >
                          No
                        </button>
                        <button
                          onClick={confirmCancelAll}
                          className="text-[10px] text-red-400 hover:text-red-300 font-medium transition-colors"
                        >
                          Yes, cancel all
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* File list */}
                <div className="max-h-64 overflow-y-auto divide-y divide-white/[0.02]">
                  {items.map((item) => (
                    <FileRow
                      key={item.id}
                      item={item}
                      onCancel={onCancelItem}
                      onRemove={onRemoveItem}
                      onRetry={onRetryItem}
                    />
                  ))}
                </div>

                {/* Footer stats */}
                <UploadStatsFooter stats={stats} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
  );

  return createPortal(panel, document.body);
};

export default BatchUploadPanel;
